import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { AuditRecord } from "@tdelivr/api-client/generated/security/audit.js";

import type { BoardRow } from "../board.ts";
import { PLATFORM_AUDIT_SCOPE } from "../navigation.ts";
import {
  actorOf,
  boundedNote,
  clockTime,
  EXPORT_REFUSAL,
  ingestionLagSeconds,
  pageBoundaryHazard,
  PER_FETCH_PARAMETER,
  statesPresent,
  stateOf,
  tenantOf,
  tenantSpanNote,
} from "../platform-audit.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `audit` in `16 WP15 Platform Machinery.dc.html`: the one page of sixteen that draws from data.
 *
 * ```
 * audit:['Platform audit','AUD-01','Who did what, across every tenant. Append-only by trigger.',
 *   [['Events today','92,118','done',''],['Cross-tenant actions','37','caution','platform staff'],
 *    ['Denied','12','closed','403s'],['Exports','1','lock','this week']],
 *   ['When','Actor','Action'],
 *   [['09:12:04','K. Otieno · ten_7Q2M','payout PYO-0928 approved','done','recorded'], …],
 *   'lock','There is no edit or delete anywhere on this page, because the store does not allow it.']
 * ```
 *
 * Every column, the state badge, the row detail panel, the closing note and the toolbar come from
 * that row. What is drawn from data and what is not:
 *
 * | drawn                                   | source                                              |
 * | --------------------------------------- | --------------------------------------------------- |
 * | the rows                                | `GET /v1/platform/audit-records` (ADR 0039)         |
 * | `When`                                  | `auditRecords.occurredAt`                           |
 * | `Actor` — `K. Otieno · ten_7Q2M`        | `actorChain[].subject` and `auditRecords.tenantId`  |
 * | `Action` and the `denied` badge         | `auditRecords.action`, `auditRecords.outcome`       |
 * | the four KPI tiles                      | **nothing**, and that is the whole of this screen's hole |
 * | `Export platform audit log` From and To | **nothing** — the listing publishes `before` only   |
 *
 * **The four tiles are not drawn, and that is the fidelity defect this screen carries.** `Events
 * today · 92,118` is a count and R-04 measured that **no collection read on this platform publishes
 * a total** — this listing publishes an array and nothing else, so a tile reading `200` would mean
 * "200 so far" under a label meaning "92,118". `Cross-tenant actions · 37` needs the actor's own
 * tenant, which an audit record does not publish (Q-174). `Denied · 12` and `Exports · 1` are counts
 * of the whole log, not of the page. A tile filled from the 200 rows on screen would be a number an
 * operator would quote. So the row of tiles is absent rather than invented, which is a visible
 * difference from the design and is recorded as such — `tooling/design-fidelity/check-platform-audit.mts`
 * reports it as the height difference it is, and Q-178 carries it.
 *
 * ## What this page does not offer, and why each absence is deliberate
 *
 * **No command.** The design's closing note is its own explanation — "There is no edit or delete
 * anywhere on this page, because the store does not allow it" — and AUD-01 enforces it with a
 * database trigger, not a convention (`tests/contract/append-only-enforcement.test.ts`). So this is
 * the one board in the product whose missing buttons are the design, and the primary control is the
 * export form, drawn and refused with `EXPORT_REFUSAL` on it.
 *
 * **No second page.** `pageBoundaryHazard()` is the measurement: the listing's only cursor is a
 * bare instant over a non-unique sort key, so following it skips every record sharing the last
 * row's millisecond. On a listing spanning every tenant that is the expected case rather than an
 * edge one. The board says so under the grid instead of offering a Next button that loses rows.
 *
 * **The scope is named here**, in `navigation.ts`'s `PLATFORM_AUDIT_SCOPE`, and asked for before the
 * call rather than discovered from a 403. That is not defensive coding: `channel-scopes.mjs` derives
 * the Keycloak client's grant from string literals in this application's own source, so a screen
 * that spends a scope without naming it gets a token that is short it — which is exactly how the
 * notification bell answered 403 on three consoles. Q-139 records that nobody has decided who may
 * hold this one.
 */

const audit = services.security__audit;

export interface PlatformAuditProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Audit › Platform audit`, which is the design's own trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Platform audit";
const SOURCE = "AUD-01";
const DESCRIPTION = "Who did what, across every tenant. Append-only by trigger.";
const COLUMNS = ["When", "Actor", "Action"] as const;
const NOTE =
  "There is no edit or delete anywhere on this page, because the store does not allow it.";
/** The design's own `noteK` for this page is `lock`, which its `NK` map draws in the accent tint. */
const NOTE_KIND = "lock" as const;
/** The design's `FORMS.audit` primary button, word for word. */
const PRIMARY_LABEL = "Export audit log";

/**
 * One record as the board's generic row.
 *
 * The detail panel's fields are the three columns plus everything else the contract publishes that an
 * operator reading an audit log would want: the event's own identifier, the correlation it belongs
 * to, the subject it is about, the resource it touched, the producer that reported it and the
 * content hash that makes the row tamper-evident. All published, none invented.
 *
 * `history` carries what the design's `History` block can be filled with and no more. That block
 * reads `Created`, `Last changed` and `Version` on every other page in the product; an audit record
 * is created once, never changed and has no version, so two of the three have no meaning rather than
 * no source — and the one that does, `Created`, is the row's own `occurredAt` with `ingestedAt`
 * beside it, because the gap between them is the only thing about this record that is not simply
 * what it says. Q-107 is where the missing actor on every other page's `History` lives; this page is
 * the one where the block genuinely has only one entry.
 */
function rowOf(record: AuditRecord, zone?: string): BoardRow {
  const state = stateOf(record);
  const lag = ingestionLagSeconds(record);
  const lagNote =
    lag === undefined
      ? undefined
      : `Recorded ${String(lag)}s after it happened. occurredAt is the producer's clock; ingestedAt is AUD-01's.`;

  return {
    // The event identifier, which AUD-01 publishes and makes unique per tenant. Prefixed with the
    // tenant because this listing spans tenants and two producers in two tenants may mint the same
    // one — a React key that collided would make one row replace another on a board whose purpose is
    // that nothing is lost.
    id: `${record.tenantId}:${record.eventId}`,
    cells: [
      {
        text: clockTime(record, zone),
        mono: true,
        ...(lagNote === undefined ? {} : { title: lagNote }),
      },
      // The design's own grammar: the actor and the tenant in one cell, separated by a bar.
      { text: `${actorOf(record)} · ${tenantOf(record)}`, mono: true },
      { text: record.action },
    ],
    state,
    // The design tints a row for its `human` and `failed` classes only. A denied action is `closed`
    // and a cross-tenant one is `caution`, so no row on this board is tinted — which is the design's
    // behaviour and not an omission: its three sample rows are untinted too.
    detail: [
      { label: "Outcome", value: record.outcome },
      { label: "Event", value: record.eventId, mono: true },
      { label: "Correlation", value: record.correlationId, mono: true },
      { label: "Subject", value: `${record.subject.type} ${record.subject.id}`, mono: true },
      {
        label: "Resource",
        value:
          record.resource === undefined
            ? ""
            : `${record.resource.type}${record.resource.id === undefined ? "" : ` ${record.resource.id}`}`,
        mono: true,
        // `resource` is optional on the wire: a record about an action with no object has none, and
        // the design's own `—` is the right mark for that rather than an empty cell.
        ...(record.resource === undefined ? { absent: true } : {}),
      },
      { label: "Reported by", value: record.producerSubject, mono: true },
      { label: "Content hash", value: record.contentHash, mono: true },
      {
        label: "Actor chain",
        value: record.actorChain.map((link) => `${link.subject} (${link.kind})`).join(" → "),
        mono: true,
        ...(record.actorChain.length === 0 ? { absent: true } : {}),
      },
    ],
    history: [
      { label: "Occurred", value: record.occurredAt, mono: true },
      { label: "Recorded", value: record.ingestedAt, mono: true },
    ],
    // The design takes the detail panel's glyph from the page's own form icon, which for this page
    // is `history`.
    glyph: "history",
  };
}

export function PlatformAudit({ transport, session, crumbs }: PlatformAuditProps): ReactNode {
  const [records, setRecords] = useState<readonly AuditRecord[] | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRecords(undefined);
    setFailure(undefined);

    /*
     * Asked for before the call, not discovered from the refusal.
     *
     * The board renders the console's own forbidden state naming the scope, which is what an
     * operator can act on — they can ask their administrator for `audit:platform:read` by name. A
     * 403 from the service would render the same state, so this is not the only guard; it is the one
     * that makes the scope a literal in this application's source, which is what
     * `channel-scopes.mjs` derives the grant from.
     */
    if (!session.scopes.has(PLATFORM_AUDIT_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [PLATFORM_AUDIT_SCOPE] });
      return;
    }

    try {
      const client = audit.createAuditClient(transport);
      /*
       * `listPlatformAuditRecords`, not `listAuditRecords`.
       *
       * The two differ by one word and by every row they answer: the second restricts results to the
       * verified token's tenant, which on this page would be a platform audit board showing one
       * tenant's log. ADR 0039 published them side by side on purpose and left the first untouched.
       *
       * `before` is deliberately not sent. See `pageBoundaryHazard`.
       */
      const page = await client.listPlatformAuditRecords(PER_FETCH_PARAMETER);
      setRecords(page.auditRecords);
    } catch (error) {
      setFailure(stateForError(error, [PLATFORM_AUDIT_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows =
    records === undefined
      ? undefined
      : records
          .filter((record) => state === "" || stateOf(record).label === state)
          .map((record) => rowOf(record));

  const hazard = records === undefined ? undefined : pageBoundaryHazard(records);
  const bounded = boundedNote(records?.length, hazard);
  const span = tenantSpanNote(records);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Nothing has been recorded yet. Every privileged action on any tenant appears here as it happens."
      primary={{ label: PRIMARY_LABEL, refusal: EXPORT_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (records === undefined ? [] : statesPresent(records)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "lock", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(span === undefined ? {} : { footnote: span })}
      exportName="platform-audit"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
